import { Box, Button, CircularProgress, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { FormikErrorNotification, FormikTextField } from "@shared";

import { FormikDateTimePicker } from "@/common/components/fields/formik/formik-datetime-picker/FormikDateTimePicker";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { useTranslation } from "@/translations";

import { useBannerConfiguration } from "./hooks/useBannerConfiguration";

function BannerConfigurationInner() {
  const { t } = useTranslation();
  const { formik, activeBanner, isLoading, handleDeactivate, isDeactivating, messageMaxLength } =
    useBannerConfiguration();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{ flex: 1, maxHeight: "100%", gap: 1, pb: 1 }}
      bgcolor={"white"}
    >
      <FormikErrorNotification formik={formik} />
      <Stack flex={1} gap={3} pt={1} sx={{ overflowY: "auto" }}>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid size={4}>
            <FormikDateTimePicker
              formik={formik}
              fieldName="bannerStartDate"
              label={t("settings.banner.configuration.bannerStart")}
              required
            />
          </Grid>
          <Grid size={4}>
            <FormikDateTimePicker
              formik={formik}
              fieldName="maintenanceStartDate"
              label={t("settings.banner.configuration.maintenanceStart")}
              required
            />
          </Grid>
          <Grid size={4}>
            <FormikDateTimePicker
              formik={formik}
              fieldName="maintenanceEndDate"
              label={t("settings.banner.configuration.maintenanceEnd")}
              required
            />
          </Grid>
          <Grid size={12}>
            <FormikTextField
              formik={formik}
              fieldName="message"
              label={t("settings.banner.configuration.message")}
              multiline
              rows={3}
              slotProps={{ input: { inputProps: { maxLength: messageMaxLength } } }}
            />
          </Grid>
        </Grid>
      </Stack>
      <Stack direction="row" justifyContent="space-between" p={0.5}>
        <Button variant="outlined" onClick={formik.handleReset} disabled={formik.isSubmitting || !formik.dirty}>
          {t("common.resetChanges")}
        </Button>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          {activeBanner && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeactivate}
              disabled={isDeactivating || formik.isSubmitting}
            >
              {t("settings.banner.configuration.deactivate")}
            </Button>
          )}
          <Button variant="contained" type="submit" disabled={formik.isSubmitting || !formik.dirty}>
            {t("common.save")}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}

export const BannerConfiguration = withAuthorization(BannerConfigurationInner, UserPermission.Supervisor);
